import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import styles from './styles.module.scss';
import { formFilters } from '@components-clean/core/api-grid/api-grid.helper';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { CreateSubjectTagModel } from '@interfaces-clean/subject-monitoring.interface';
import { isValidTagLabel, isValidTagLength } from '@utils-clean/validation';
import { useLocalState, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { TagAPI } from '@apis/bff-api/tag';

type AddSubjectTagDialogProps = {
    subjectCodes: string[];
    onClose: () => void;
    onSubmit: () => void;
    studyRef: string;
    datasetId: number;
    isEditSubject?: boolean;
    isSubjectProfile?: boolean;
    tagAll?: boolean;
    parentFilter?: React.RefObject<CompositeFilterDescriptor | null>;
    comparisonDataset?: number;
    totalRows?: number;
};

interface MultiSelectOption {
    id: number;
    value: string;
    active?: boolean;
}

const COMPONENT_NAME = 'add-edit-subject-tags-dialog';

const AddEditSubjectTagsDialog = ({
    subjectCodes,
    onClose,
    onSubmit,
    studyRef,
    datasetId,
    isEditSubject,
    isSubjectProfile,
    tagAll,
    parentFilter,
    comparisonDataset,
    totalRows,
}: AddSubjectTagDialogProps) => {
    const subjectCode = subjectCodes.length === 1 ? subjectCodes[0] : null;

    // Context hooks
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const { state } = useLocalState();
    const { sessionId } = useStudyDataFilterContext();

    // Translations
    const { t } = useTranslation();
    const { t: smTagDialog }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'subjectTagDialog',
    });
    const { t: smProfileTagDialog }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'subjectProfileTagDialog',
    });

    // APIs
    const { isMutating, trigger: getAllTags } = TagAPI.useGetAllTags(studyRef);
    const { trigger: createBatchTagInstances, isMutating: isSaving } =
        SubjectMonitoringAPI.useCreateBatchTagInstances(studyRef);
    const { trigger: updateSubjectTagInstances, isMutating: isUpdating } =
        SubjectMonitoringAPI.useUpdateSubjectTagInstances(studyRef);
    const { trigger: getSelectedTagsForSubject, isMutating: isLoading } =
        SubjectMonitoringAPI.useGetSelectedTagsForSubject(studyRef, subjectCode);

    const [tags, setTags] = useState<MultiSelectOption[]>([]);
    const allSubjectTags = useRef<MultiSelectOption[]>([]);
    const allInactiveTags = useRef<MultiSelectOption[]>([]);
    const subjectInactiveTags = useRef<MultiSelectOption[]>([]);

    const subjectCount = tagAll === true ? (totalRows ?? 0) : subjectCodes.length;
    const subjectsPlural = `${subjectCount > 1 ? 'subjects' : 'subject'}`;
    const infoMessage =
        tagAll === true
            ? smTagDialog('tagAllWarning')
            : t('sm:subjectTagDialog.infoMessage', {
                  subjectCount,
                  subjectsPlural,
              });

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty },
        reset,
    } = useForm<{
        options: MultiSelectOption[];
    }>({
        mode: 'all',
        defaultValues: {
            options: [],
        },
    });

    const [showTagAllConfirmation, setShowTagAllConfirmation] = useState(false);

    const onCloseHandler = async () => {
        if (showTagAllConfirmation) {
            setComponentHasChanges(COMPONENT_NAME, false);
            onClose();
        } else {
            if (componentHasChanges(COMPONENT_NAME)) {
                const confirmed = await confirmDiscardChanges('add-subject-tag-dialog-discard-changes');
                if (!confirmed) {
                    return;
                }
                setComponentHasChanges(COMPONENT_NAME, false);
            }
            onClose();
        }
    };

    useEffect(() => {
        getAllTags()
            .then((loadedData) => {
                let sortedLabels: MultiSelectOption[] = (loadedData ?? [])
                    .map((tag) => ({ id: tag.id as number, value: tag.label as string, active: tag.active as boolean }))
                    .sort((a, b) =>
                        a.value.localeCompare(b.value, undefined, {
                            numeric: true,
                            sensitivity: 'base',
                        }),
                    );

                // when tagging multiple - only show active
                if (!isEditSubject && !isSubjectProfile) {
                    sortedLabels = sortedLabels.filter((tag) => tag.active);
                }

                if (subjectCode && isEditSubject) {
                    // We're editing a single subject's tags
                    getSelectedTagsForSubject()
                        .then((selectedTagIdsForSubject) => {
                            const tagsNew = sortedLabels
                                .filter((x) => selectedTagIdsForSubject.includes(x.id))
                                .map((tag) => ({
                                    id: tag.id as number,
                                    value: tag.value as string,
                                    active: sortedLabels.find((x) => x.active === tag.active)?.active as boolean,
                                }));

                            // Tags list should only include all active AND assigned inactive
                            const updatedLabels: MultiSelectOption[] = [];
                            sortedLabels.forEach((sortedLabel) => {
                                if (
                                    sortedLabel.active ||
                                    (!sortedLabel.active && selectedTagIdsForSubject.some((x) => x === sortedLabel.id))
                                ) {
                                    updatedLabels.push(sortedLabel);
                                }
                            });
                            setTags(updatedLabels);
                            subjectInactiveTags.current = updatedLabels.filter((subjectTag) => !subjectTag.active);
                            allSubjectTags.current = [...tagsNew];
                            reset({ options: tagsNew }, { keepDirty: false });
                        })
                        .catch((err) => window.console.log(err));
                } else {
                    setTags(sortedLabels);
                }

                // Always set inactive tags as we need to check against them
                allInactiveTags.current = (loadedData ?? [])
                    .filter((x) => !x.active)
                    .map((tag) => ({
                        id: tag.id as number,
                        value: tag.label as string,
                        active: tag.active as boolean,
                    }));
            })
            .catch((err) => window.console.log(err));
    }, [isEditSubject, isSubjectProfile, getAllTags, getSelectedTagsForSubject, reset, subjectCode]);

    const addBatchTagInstances = (tagsToAdd: CreateSubjectTagModel) => {
        createBatchTagInstances(tagsToAdd)
            .then((response: boolean) => {
                if (!response) {
                    ShowError(t('errorMessage'));
                } else {
                    ShowSuccess(t('actionsSuccessfulToast'));
                }
                setComponentHasChanges(COMPONENT_NAME, false);
                onSubmit();
            })
            .catch(() => {
                ShowError(t('errorMessage'));
                setComponentHasChanges(COMPONENT_NAME, false);
                onClose();
            });
    };

    const updateSubjectTags = (tagsToAdd: CreateSubjectTagModel) => {
        updateSubjectTagInstances(tagsToAdd)
            .then((response: boolean) => {
                if (!response) {
                    ShowError(t('errorMessage'));
                } else {
                    ShowSuccess(t('actionsSuccessfulToast'));
                }
                setComponentHasChanges(COMPONENT_NAME, false);
                onSubmit();
            })
            .catch(() => {
                ShowError(t('errorMessage'));
                setComponentHasChanges(COMPONENT_NAME, false);
                onClose();
            });
    };

    const isDataLoading = isSaving || isMutating || isLoading || isUpdating;

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty, setComponentHasChanges]);

    let title;
    if (isSubjectProfile) {
        const datasetDate = state?.selectedCM2Dataset?.dataCutDate;
        title = datasetDate ? `${smProfileTagDialog('title')} - ${datasetDate}` : smProfileTagDialog('title');
    } else {
        title = tagAll ? smTagDialog('tagAllTitle') : smTagDialog('tagSelectedTitle');
    }

    return (
        <DraggableDialog
            dataTestId="create-subject-tag-dialog"
            onClose={onCloseHandler}
            onSubmit={() =>
                handleSubmit(async (values) => {
                    if (tagAll === true && !showTagAllConfirmation) {
                        setShowTagAllConfirmation(true);
                        return;
                    }

                    if (tagAll === true && showTagAllConfirmation) {
                        setShowTagAllConfirmation(false);
                    }

                    const newTags = Array.isArray(values.options) ? values.options : [values.options];
                    const filtersData = parentFilter?.current
                        ? formFilters(parentFilter?.current, 'datatable')
                        : undefined;

                    const tagsToAdd: CreateSubjectTagModel = {
                        datasetId: datasetId as number,
                        tags: newTags.map((tag) => ({
                            label: tag.value,
                        })),
                        subjectCodes,
                        tagAll,
                        sessionId,
                        filters: { filters: filtersData },
                        comparisonDatasetId: comparisonDataset,
                    };

                    if (isEditSubject) {
                        updateSubjectTags(tagsToAdd);
                    } else {
                        addBatchTagInstances(tagsToAdd);
                    }
                })()
            }
            title={title}
            primaryBtnDisabled={!isDirty || !isValid || isUpdating || isSaving}
            primaryBtnLabel={showTagAllConfirmation ? t('common:continue') : t('common:save')}
            height={showTagAllConfirmation ? 180 : 225}
            width={600}
            secondaryBtnDisabled={isDataLoading}
        >
            <form id="add-subject-tag-form" className="k-w-full k-d-flex k-flex-column k-gap-2">
                {showTagAllConfirmation && (
                    <div className="k-d-grid k-pb-2">
                        <div className={styles.confirmMessage}>
                            {t('sm:subjectTagDialog.tagAllConfirmMessage', { subjectCount })}
                        </div>
                    </div>
                )}
                {!showTagAllConfirmation && (
                    <>
                        {!isSubjectProfile && (
                            <div className="k-d-grid k-pb-2">
                                <InfoField message={infoMessage} layout="stretch" type={tagAll ? 'Warning' : 'Info'} />
                            </div>
                        )}

                        <MultiSelectController
                            maxLines={2}
                            loading={isDataLoading}
                            isEditing
                            control={control}
                            name="options"
                            data={tags}
                            data-testid="tagLabel"
                            label={smTagDialog('tagsLabel')}
                            disabled={isDataLoading}
                            textField="value"
                            dataItemKey="id"
                            activeField="active"
                            handleInputChange={(event) => {
                                let uniqueTags: MultiSelectOption[] = event.value.filter(
                                    (tag: MultiSelectOption, index: number, self: MultiSelectOption[]) =>
                                        index ===
                                        self.findIndex(
                                            (c: MultiSelectOption) =>
                                                c.value.trim().toLowerCase() === tag.value.trim().toLowerCase(),
                                        ),
                                );

                                /*
                                 * If we've removed a tag and re-added the same one it will have a new ID
                                 * Go and find it in the original tags and update the ID.
                                 */
                                uniqueTags.forEach((uniqueTag) => {
                                    let foundTag = tags.find((tagToFind) => tagToFind.value === uniqueTag.value);
                                    if (!foundTag) {
                                        foundTag = allInactiveTags.current.find(
                                            (tagToFind) => tagToFind.value === uniqueTag.value,
                                        );
                                    }
                                    if (foundTag) {
                                        uniqueTag.id = foundTag.id;
                                        uniqueTag.active = foundTag.active;
                                    }
                                });

                                uniqueTags = uniqueTags.map((x) => {
                                    return { id: x.id, active: x.active as boolean, value: x.value };
                                });

                                /*
                                 * Edge case for when we've removed an inactive tag that was assigned to a subject
                                 * and want to re-add it, and that is the only action we have done.
                                 */
                                if (uniqueTags.length === allSubjectTags.current.length) {
                                    const uniqueTagIds = new Set(uniqueTags.map((tag) => tag.id));
                                    const subjectTagIds = new Set(allSubjectTags.current.map((tag) => tag.id));
                                    const noChangesMade = uniqueTagIds.difference(subjectTagIds).size === 0;
                                    if (noChangesMade) {
                                        reset({ options: uniqueTags }, { keepDirty: false });
                                    }
                                }

                                /*
                                 * If we've removed an inactive one, remove it from the available tags so it's not selectable
                                 */
                                setTags((allTags) => {
                                    return allTags.filter(
                                        (tag) =>
                                            tag.active ||
                                            uniqueTags.find((uniqueTag) => uniqueTag.id === tag.id) ||
                                            subjectInactiveTags.current.includes(tag),
                                    );
                                });
                            }}
                            filterable={false}
                            validateRules={{
                                noInvalidCharacters: (value) => {
                                    const tagList = value as MultiSelectOption[] | null;
                                    if (tagList && tagList.some((tag) => !isValidTagLabel(tag.value))) {
                                        return smTagDialog('invalidChars');
                                    }
                                    return true;
                                },
                                checkExistingTags: (value) => {
                                    const tagList = value as MultiSelectOption[] | null;
                                    if (tagList) {
                                        const existsAndIsActive = tagList.some((tag, index) => {
                                            const normalizedValue = tag.value.trim().toLowerCase();

                                            // Check against already saved to the DB tags
                                            const existsInDb = tags.some(
                                                (existingTag) =>
                                                    existingTag.value.trim().toLowerCase() === normalizedValue &&
                                                    existingTag.id !== tag.id,
                                            );

                                            // Check the new local list of tags
                                            const existsInUi = tagList.some(
                                                (localExistingTag, i) =>
                                                    i !== index &&
                                                    localExistingTag.value.trim().toLowerCase() === normalizedValue,
                                            );

                                            return existsInDb || existsInUi;
                                        });

                                        if (existsAndIsActive) {
                                            return smTagDialog('tagAlreadyExists');
                                        }

                                        /*
                                         * This validation check does not know if we're trying to add a new inactive tag,
                                         * or it exists already against the subject, so need to compare against what they
                                         * had originally.
                                         */
                                        const sameTags = (tagA: MultiSelectOption, tagB: MultiSelectOption) =>
                                            tagA.value.trim().toLowerCase() === tagB.value.trim().toLowerCase();

                                        const tryingToAddNewInactiveTag = tagList.some(
                                            (tag) =>
                                                // exists in all inactive tags (parent tags)...
                                                allInactiveTags.current.some((inactiveTag) =>
                                                    sameTags(inactiveTag, tag),
                                                ) &&
                                                // but did not already exist against the subject
                                                !subjectInactiveTags.current.some((inactiveTag) =>
                                                    sameTags(inactiveTag, tag),
                                                ),
                                        );

                                        if (tryingToAddNewInactiveTag) {
                                            return smTagDialog('tagAlreadyExistsButIsInactive');
                                        }
                                    }

                                    return true;
                                },
                                tagCharLimit: (value) => {
                                    const tagList = value as MultiSelectOption[] | null;
                                    if (tagList && tagList.some((tag) => !isValidTagLength(tag.value))) {
                                        return smTagDialog('characterLimitExceeded');
                                    }
                                    return true;
                                },
                                tagCountLimit: (value) => {
                                    if (tagAll) {
                                        const tagList = value as MultiSelectOption[] | null;
                                        if (tagList !== null && tagList.length > 1)
                                            return smTagDialog('tagCountLimitExceeded');
                                    }
                                    return true;
                                },
                            }}
                        />
                    </>
                )}
            </form>
        </DraggableDialog>
    );
};

export default AddEditSubjectTagsDialog;
